import React, { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Box,
  InputBase,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  ClickAwayListener,
  CircularProgress,
  Button,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import ProductModal from "./product/ProductModal";
import { fetchProductData } from "../api/service";

const SearchBar = () => {
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Debounce search term to avoid too many API calls while typing
  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchValue);
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchValue]);

  // Use React Query to fetch products with server-side search
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["product-search", debouncedSearchTerm],
    queryFn: () => fetchProductData({ 
      page: 1, 
      pageSize: 20, 
      name: debouncedSearchTerm,
      sortBy: "brandName",
      sortOrder: "ASC" 
    }),
    enabled: debouncedSearchTerm.length > 1, // Only search if term is at least 2 chars
    staleTime: 60 * 1000, // 1 minute
    cacheTime: 5 * 60 * 1000, // 5 minutes
  });

  // Memoize products to prevent unnecessary re-renders
  const searchResults = useMemo(() => {
    return data?.products || [];
  }, [data]);

  // Update suggestions when search results change
  useEffect(() => {
    setSuggestions(searchResults);
    setShowSuggestions(!!searchValue && searchResults.length > 0);
  }, [searchResults, searchValue]);

  const handleInputChange = (e) => {
    setSearchValue(e.target.value);
    if (e.target.value === '') {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (product) => {
    setSearchValue(product.brandName);
    setShowSuggestions(false);
    setSelectedProduct(product);
  };

  const handleSearch = () => {
    if (!searchValue || !searchResults.length) return;

    const matchingProduct = searchResults.find(
      (product) =>
        product.brandName.toLowerCase() === searchValue.toLowerCase() ||
        product.genericName.toLowerCase() === searchValue.toLowerCase()
    );

    if (matchingProduct) {
      setSelectedProduct(matchingProduct);
    }
  };

  const handleCloseModal = () => {
    setSelectedProduct(null);
  };

  const handleRefresh = () => {
    refetch();
  };

  return (
    <>
      <ClickAwayListener onClickAway={() => setShowSuggestions(false)}>
        <Box
          className="search-bar"
          sx={{ 
            border: "1px solid #215d66", 
            margin: "10px 0", 
            width: "100%",
            position: "relative",
            display: "flex"
          }}
        >
          <InputBase
            placeholder="Search by brand or generic name"
            inputProps={{ "aria-label": "search" }}
            className="search-input"
            value={searchValue}
            onChange={handleInputChange}
            onKeyPress={(e) => e.key === "Enter" && handleSearch()}
            sx={{ ml: 1, flex: 1 }}
          />
          <IconButton
            type="submit"
            aria-label="search"
            className="search-button"
            onClick={handleSearch}
          >
            <SearchIcon />
          </IconButton>
          <IconButton
            aria-label="refresh products"
            onClick={handleRefresh}
            title="Refresh product list"
            sx={{ 
              padding: "4px",
              marginRight: "4px",
              color: "#215d66"
            }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>

          {isLoading && debouncedSearchTerm && (
            <CircularProgress 
              size={20}
              sx={{ 
                position: 'absolute',
                right: '80px',
                top: '50%',
                transform: 'translateY(-50%)'
              }} 
            />
          )}

          {showSuggestions && suggestions.length > 0 && (
            <Paper
              elevation={3}
              sx={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                zIndex: 1,
                mt: 1,
                maxHeight: "300px",
                overflow: "auto",
              }}
            >
              <List>
                {suggestions.map((product) => (
                  <ListItem
                    key={product.id}
                    button
                    onClick={() => handleSuggestionClick(product)}
                    sx={{
                      "&:hover": {
                        backgroundColor: "#f5f5f5",
                      },
                    }}
                  >
                    <ListItemText
                      primary={product.brandName}
                      secondary={product.genericName}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}
        </Box>
      </ClickAwayListener>

      {selectedProduct && (
        <ProductModal
          product={selectedProduct}
          handleClose={handleCloseModal}
        />
      )}
    </>
  );
};

export default SearchBar;
